import {
  SEO_DEFAULT_LOCALE,
  SEO_SUPPORTED_LOCALES,
  buildAlternateLinks,
  canonicalizeUrl,
  ensureValidSiteUrl,
  evaluateRouteIndexability,
  resolveContentLanguageFromPath
} from '~/utils/websiteSeo';

type SeoText = string | (() => string);
type SeoOgType = 'website' | 'article';

interface UseWebsiteSeoOptions {
  title: SeoText;
  description: SeoText;
  path?: string;
  /** 分页页码：当 >1 时自动在 title 末尾追加 " - 第N页" / " - Page N" */
  pageNumber?: number;
  forceNoIndex?: boolean;
  includeAlternates?: boolean;
  availableLocales?: string[];
  ogType?: SeoOgType;
  twitterCard?: string;
}

function resolveSeoText(value: SeoText): string {
  return typeof value === 'function' ? value() : value;
}

/**
 * 为分页标题追加页码后缀。
 * 第 1 页不加后缀：避免与主列表页产生重复 <title>，防止搜索引擎判定为重复内容。
 * 格式: 中文 "博客 - 第2页 | OLTranslation", 英文 "Blog - Page 2 | OLTranslation"
 */
function appendPageSuffix(title: string, pageNumber: number | undefined, locale: string): string {
  if (!pageNumber || pageNumber <= 1) {
    return title;
  }
  return locale === 'en' ? `${title} - Page ${pageNumber}` : `${title} - 第${pageNumber}页`;
}

export const useWebsiteSeo = (options: UseWebsiteSeoOptions) => {
  const route = useRoute();
  const runtimeConfig = useRuntimeConfig();
  const siteUrl = ensureValidSiteUrl(runtimeConfig.public.siteUrl as string | undefined);

  useHead(() => {
    const currentPath = options.path ?? route.path;
    const canonical = canonicalizeUrl({
      siteUrl,
      path: currentPath,
      query: route.query
    });
    const policy = evaluateRouteIndexability(currentPath);
    const noIndex = options.forceNoIndex === true || !policy.shouldIndex;
    const shouldOutputAlternates = options.includeAlternates !== false && !noIndex;
    const availableLocales = options.availableLocales ?? SEO_SUPPORTED_LOCALES;
    const ogType = options.ogType ?? 'website';
    const twitterCard = String(options.twitterCard ?? 'summary_large_image');
    const contentLanguage = resolveContentLanguageFromPath(currentPath, {
      locales: SEO_SUPPORTED_LOCALES,
      defaultLocale: SEO_DEFAULT_LOCALE
    });

    // 从 contentLanguage 推导 localeCode（zh-CN → zh, en-US → en），避免重复调用 resolveContentLanguageFromPath
    const localeCode = contentLanguage === 'en-US' ? 'en' : 'zh';
    const rawTitle = resolveSeoText(options.title);
    const displayTitle = appendPageSuffix(rawTitle, options.pageNumber, localeCode);

    const links: Array<{ rel: string; href: string; hreflang?: string }> = [
      { rel: 'canonical', href: canonical }
    ];

    if (shouldOutputAlternates) {
      const alternates = buildAlternateLinks({
        siteUrl,
        path: currentPath,
        availableLocales,
        defaultLocale: SEO_DEFAULT_LOCALE
      });
      for (const alternate of alternates) {
        links.push({
          rel: 'alternate',
          hreflang: alternate.hreflang,
          href: alternate.href
        });
      }
    }

    const meta: Array<{ name?: string; property?: string; 'http-equiv'?: string; content: string }> = [
      { name: 'description', content: resolveSeoText(options.description) },
      { property: 'og:title', content: displayTitle },
      { property: 'og:description', content: resolveSeoText(options.description) },
      { property: 'og:type', content: ogType },
      { property: 'og:url', content: canonical },
      { 'http-equiv': 'content-language', content: contentLanguage },
      { name: 'twitter:card', content: twitterCard },
      { name: 'twitter:title', content: displayTitle },
      { name: 'twitter:description', content: resolveSeoText(options.description) }
    ];

    if (noIndex) {
      meta.push({ name: 'robots', content: 'noindex' });
    }

    return {
      title: displayTitle,
      htmlAttrs: {
        lang: contentLanguage
      },
      meta,
      link: links
    };
  });
};
